import {
  Button,
  ChakraModal,
  FeaturedIcons,
  FiAlertTriangle,
  Flex,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@agrotoken-tech/ui';
import { useToastsHook, useTranslation } from '@bcba/react-hooks';
import { useMutation } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { markAsPaidCropPledge } from '../../../../services/crop-pledge/mark-as-paid-crop-pledge';
import { useCropPledgeContext } from '../context/CropPledgeContext';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const MarkAsPaidModal = ({ isOpen, onClose }: Props) => {
  const { showSuccessToast } = useToastsHook();

  const { t } = useTranslation();
  const prefix = 'cropPledge.actions.markAsPaid.modal';

  const params = useParams();
  const cropPledgeUuid = params['uuid'] ?? '';

  const { cropPledgeStatus, refetchCropPledgeStatus } = useCropPledgeContext();

  const { internalIdentifier } = cropPledgeStatus ?? {};

  const { mutate, isPending: isPendingRegister } = useMutation({
    mutationKey: ['markAsPaidCropPledge'],
    mutationFn: () => markAsPaidCropPledge(cropPledgeUuid),
    onSuccess: () => {
      showSuccessToast({
        title: t(`${prefix}.success.title`).replace(
          '{{id}}',
          internalIdentifier ?? '-',
        ),
      });
      refetchCropPledgeStatus();
      onClose();
    },
  });

  return (
    <ChakraModal isOpen={isOpen} onClose={onClose} size={'xl'}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>
          <Stack gap={4}>
            <FeaturedIcons icon={FiAlertTriangle} variant="primary" />
            <Text size="lg" variant="medium">
              {t(`${prefix}.title`)}
            </Text>
            <Text whiteSpace="break-spaces" variant="regular" color="gray.600">
              {t(`${prefix}.text`)}
            </Text>
          </Stack>
        </ModalHeader>
        <ModalFooter>
          <Flex gap={4}>
            <Button variant={'secondary'} size={'lg'} onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              variant={'primary'}
              size={'lg'}
              onClick={() => mutate()}
              isLoading={isPendingRegister}
            >
              {t(`${prefix}.confirmLabel`)}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </ChakraModal>
  );
};

export default MarkAsPaidModal;
